import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import Notification from "components/NotificationCenter/Notification";
import EmptyState from "components/shared/EmptyState";
import { getCognitoUserAttribute } from "helpers/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { INotification } from "store/notificationStore";
import { markAllNotifAsSeen } from "store/notificationStore";
import { useAppDispatch } from "store/store";

import NotificationNavbar from "./NotificationNavbar";

type TProps = {
    notifications: INotification[];
};

const NotificationPage = ({ notifications }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { user } = useAuthenticator((context) => [context.user]);
    const userAttributes = getCognitoUserAttribute("custom:accountType", user);
    const baseURL =
        userAttributes === "beneficiary"
            ? import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT
            : ["publisher", "admin"].includes(userAttributes)
              ? import.meta.env.REACT_APP_API_ENDPOINT
              : "";

    const [filterStatus, setFilterStatus] = useState<"all" | "notseen" | "seen">("all");
    const [filterType, setFilterType] = useState<"document" | "account" | "other" | "">("");
    const [notifsFiltered, setNotifsFiltered] = useState<INotification[]>([]);

    useEffect(() => {
        if (notifications?.length) {
            setNotifsFiltered(
                notifications
                    .filter((notif) =>
                        filterStatus === "all"
                            ? notif
                            : filterStatus === "notseen"
                              ? !notif.seen
                              : filterStatus === "seen" && notif.seen,
                    )
                    .filter((notif) =>
                        filterType === "document"
                            ? notif.actions.find((act) => act.data === "document")
                            : filterType === "account"
                              ? notif.actions.find((act) => act.data === "beneficiary" || act.data === "profile")
                              : filterType === "other"
                                ? notif.actions.find((act) => act.data === "none")
                                : notif,
                    ),
            );
        }
    }, [filterStatus, filterType, notifications]);

    const handleMarkAllAsRead = () => {
        if (!!baseURL && !!notifications.filter((notif) => !notif.seen).length) {
            dispatch(
                markAllNotifAsSeen({
                    baseUrl: baseURL,
                    id: notifications.filter((notif) => !notif.seen).map((notif) => notif.id),
                }),
            );
        }
    };

    return (
        <div className="notification-page">
            <NotificationNavbar
                filterStatus={filterStatus}
                filterType={filterType}
                setFilterStatus={setFilterStatus}
                setFilterType={setFilterType}
            />
            <div className="notification-container">
                <div className="notification-header">
                    <h3>
                        {filterStatus === "all"
                            ? t("Notifications.AllNotifications")
                            : filterStatus === "notseen"
                              ? t("Notifications.TitleHeader")
                              : filterStatus === "seen" && t("Notifications.AlreadyProcess")}
                        {!!notifsFiltered.length && ` (${notifsFiltered.length})`}
                    </h3>
                    {!!(filterStatus !== "seen" && notifsFiltered.filter((notif) => !notif.seen).length) && (
                        <button onClick={() => handleMarkAllAsRead()}>
                            <CheckOutlined />
                            {t("Notifications.MarkAllAsRead")}
                        </button>
                    )}
                </div>
                <div className="notification-body">
                    {notifsFiltered.length ? (
                        notifsFiltered.map((notif, i) => <Notification data={notif} key={i} />)
                    ) : (
                        <EmptyState type="notifications" sx={{ margin: "88px auto 0" }} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationPage;
